import { useState } from 'react'
import type { ApiKey } from '@creanizer/utils'
import { useSettingsStore } from '../../stores/settingsStore'

function formatDate(date?: Date): string {
  return date ? new Date(date).toLocaleString('ja-JP') : '-'
}

function getRateLimitStatus(key: ApiKey): string | null {
  if (!key.rateLimitedUntil) return null
  const remaining = new Date(key.rateLimitedUntil).getTime() - Date.now()
  return remaining > 0
    ? `レート制限中（残り${Math.ceil(remaining / 60000)}分）`
    : null
}

export function ApiKeyList({ apiKeys }: { apiKeys: ApiKey[] }) {
  const { deleteApiKey, setActiveApiKey, updateApiKey } = useSettingsStore()
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  const saveName = async (key: ApiKey) => {
    if (editingKey !== key.id) {
      setEditingKey(key.id)
      setEditingName(key.name)
      return
    }
    if (!editingName.trim()) return
    try {
      await updateApiKey(key.id, { name: editingName.trim() })
      setEditingKey(null)
      setEditingName('')
    } catch (error) {
      alert(error instanceof Error ? error.message : '名前の変更に失敗しました')
    }
  }

  const removeKey = async (id: string) => {
    if (deleteConfirmId !== id) {
      setDeleteConfirmId(id)
      window.setTimeout(() => setDeleteConfirmId(null), 3000)
      return
    }
    try {
      await deleteApiKey(id)
      setDeleteConfirmId(null)
    } catch (error) {
      alert(error instanceof Error ? error.message : 'APIキーの削除に失敗しました')
    }
  }

  const activateKey = async (id: string) => {
    try {
      await setActiveApiKey(id)
    } catch (error) {
      alert(error instanceof Error ? error.message : 'APIキーの切り替えに失敗しました')
    }
  }

  if (apiKeys.length === 0) {
    return <div className="py-8 text-center text-gray-500">APIキーがありません</div>
  }

  return (
    <div className="max-h-[420px] space-y-3 overflow-y-auto pr-2">
      {apiKeys.map(key => {
        const rateLimitStatus = getRateLimitStatus(key)
        return (
          <div
            key={key.id}
            className={`rounded border p-4 ${
              key.isActive ? 'border-green-500 bg-green-50' : 'border-gray-300'
            } ${rateLimitStatus ? 'opacity-60' : ''}`}
          >
            <div className="flex flex-col justify-between gap-3 md:flex-row">
              <div>
                {editingKey === key.id ? (
                  <input
                    className="rounded border px-2 py-1"
                    value={editingName}
                    onChange={event => setEditingName(event.target.value)}
                  />
                ) : (
                  <h3 className="font-semibold">
                    {key.name}{key.isActive ? '（使用中）' : ''}
                  </h3>
                )}
                <div className="mt-2 text-sm text-gray-600">
                  <div>APIキー: {key.key.substring(0, 10)}...</div>
                  <div>使用回数: {key.usageCount}回</div>
                  <div>最終使用: {formatDate(key.lastUsedAt)}</div>
                  <div>作成日: {formatDate(key.createdAt)}</div>
                  {rateLimitStatus && <div className="text-red-600">{rateLimitStatus}</div>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {!key.isActive && (
                  <button
                    className="btn btn-secondary text-sm"
                    disabled={Boolean(rateLimitStatus)}
                    onClick={() => void activateKey(key.id)}
                  >
                    使用する
                  </button>
                )}
                <button
                  className="btn btn-secondary text-sm"
                  onClick={() => void saveName(key)}
                >
                  {editingKey === key.id ? '保存' : '名前変更'}
                </button>
                <button
                  className="rounded bg-gray-200 px-3 py-1 text-sm hover:bg-gray-300"
                  onClick={() => void removeKey(key.id)}
                >
                  {deleteConfirmId === key.id ? 'もう一度押して削除' : '削除'}
                </button>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
